'use client';

/**
 * The leads board row (LF-5986 / LEADS-19) — `functional-spec.md` §3.4–§3.6.
 *
 * ```
 * [flag] [type] Estate of Evelyn Prosser [#REF] [unread] [awaiting] [CA] [🎙] [reason] grey note │ Form │ Meeting │ Age │ Solicitor │ ⋯
 * └────────────────── identity: flexes, truncates ──────────────────────────────────────────────┘ └──── fixed, right-aligned ────┘
 * ```
 *
 * This replaces LEADS-18's geometry-only placeholder, as that file said it
 * would: the fifteen signals, the colour grammar and the `⋯` menu now render,
 * and the provisional `formatForm` / `formatMeeting` stand-ins are gone. What is
 * kept verbatim is the seam LEADS-18 published — `LeadRow({ lead, onOpen })`
 * with `onOpen` taking the row, and `leadRowTitle` exported for the drawer and
 * the capture modal.
 *
 * **One line, truncating**, matching every other list surface in the app. The
 * whole row is clickable and keyboard-activatable; every interactive child stops
 * propagation so it cannot open the drawer behind its own dialog.
 *
 * **It renders the server's row and derives nothing.** Section, stall, effective
 * stage and cost-agreement standing all arrive settled on the DTO — the module's
 * founding rule, because the prototype's worst defect was two surfaces deriving
 * the same fact differently. Formatting *is* the frontend's, and lives in
 * `./lead-row-format`; the enum copy in `./lead-labels`.
 *
 * Widths, the gap and the two reserved slots come from `./lead-columns` and are
 * shared with the band labels — **never restated here**, because a duplicated
 * width drifts silently off the label above it and nothing (typecheck, lint or a
 * DOM assertion) can see it.
 */

import { BellIcon, PlusIcon, SendIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useListRowRhythm } from '@/components/shared/list-row';
import type { LeadBoardRow } from '@/lib/services/leads/types';
import { TeamAvatars } from '@/components/ui/things/list-card/team-avatars';
import { LEAD_COLUMNS, LEAD_COLUMN_GAP, LeadColumnCell } from './lead-columns';
import {
  LeadClosedReason,
  LeadCostAgreementSignal,
  LeadFlagCell,
  LeadMatterChip,
  LeadOutstandingSignal,
  LeadScribeGlyph,
  LeadSecondaryNote,
  LeadNewDocumentsSignal,
  LeadTypeIcon,
  LeadUnreadReplySignal
} from './lead-row-signals';
import { LeadRowActions, type LeadRowActionHandlers } from './lead-row-actions';
import {
  formatDays,
  formatMeetingCell,
  formatSpelledDateTime,
  userDisplayName
} from './lead-row-format';

/**
 * §3.5.1 — `Estate of {deceasedName}`, falling back to
 * `Enquiry from {clientNames}` when no deceased is known.
 *
 * One weight for every title: bolding on unread was tried and read as a
 * rendering inconsistency rather than as a signal. The fallback is not cosmetic
 * — a lead can be captured from a name and a note alone, so the deceased is
 * frequently unknown on the first call.
 *
 * Exported because the drawer and the capture modal need the same title.
 * Takes the **two fields the rule actually reads** rather than a whole
 * `LeadBoardRow`, so the capture modal's duplicate warning (LF-5987 /
 * LEADS-20) can name a `LeadDuplicateCandidate` — a different, much smaller
 * payload — through this same function. A candidate named by a second
 * hand-rolled title would read differently from the row it refers to, which is
 * exactly the drift this export exists to prevent.
 */
export function leadRowTitle(
  lead: Pick<LeadBoardRow, 'deceasedName' | 'clientNames'>
): string {
  return lead.deceasedName
    ? `Estate of ${lead.deceasedName}`
    : `Enquiry from ${lead.clientNames}`;
}

export interface LeadRowProps extends LeadRowActionHandlers {
  lead: LeadBoardRow;
  /**
   * Opens the lead. **Published to LEADS-21 (LF-5988)**, which mounts the drawer
   * behind it — the board owns which lead is selected, the drawer owns what that
   * looks like.
   */
  onOpen?: (lead: LeadBoardRow) => void;
  /** Flags / unflags the lead. Absent renders the mark without a control. */
  onToggleFlag?: () => void;
  /**
   * "Now", for deciding whether an upcoming meeting reads `Today`. Injectable so
   * a test pins it instead of racing midnight.
   */
  now?: Date;
}

export function LeadRow({
  lead,
  onOpen,
  onToggleFlag,
  now,
  ...actions
}: LeadRowProps) {
  const closed = !!lead.outcome;

  /* Row height and title size from the app-wide rhythm — see `shared/list-row`
     and VALE-1534. */
  const rhythm = useListRowRhythm();

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open lead: ${leadRowTitle(lead)}`}
      data-testid={`lead-row-${lead.id}`}
      onClick={() => onOpen?.(lead)}
      onKeyDown={(e) => {
        /*
          **Only the row's own keypress opens the drawer.** A keydown bubbles,
          and every interactive child here stops propagation on *click* — which
          cannot see a key. So without this the flag, the `⋯` trigger, the
          `Send` / `+ Meeting` pills and the meeting cell each opened the drawer
          when activated from the keyboard.

          The stray open is the lesser half. `preventDefault` below also
          suppresses the browser's synthesised click, so the child's own action
          never ran at all — pressing Enter on the flag opened the lead
          *instead of* flagging it.

          Guarded here rather than by adding an `onKeyDown` to each child: the
          rule is "this handler belongs to the row", and stating it once is what
          keeps the next control added to this row from inheriting the bug.
        */
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(lead);
        }
      }}
      className={cn(
        'flex cursor-pointer items-center px-4 text-left transition-colors',
        rhythm.paddingY,
        LEAD_COLUMN_GAP,
        /*
          §3.6 — **a flagged lead washes the whole row amber**, the only signal
          on this board that colours the row rather than a mark inside it. That
          weight is right for the one thing she declared herself: it survives
          peripheral vision, which is the entire point of flagging something.

          `amber-50` rather than a stronger step, because two things must stay
          legible on top of the tint: the amber `Pending cost agreement` pill and
          the bold amber stalled age. The hover has to be amber too — falling
          back to `hover:bg-slate-50` would have the row lose its flag under the
          pointer, which reads as the flag being cleared by hovering.
        */
        lead.flagged
          ? 'bg-warning-subtle hover:bg-warning-subtle-border'
          : 'hover:bg-surface-subtle',
        closed && 'opacity-75'
      )}
    >
      <LeadFlagCell row={lead} onToggle={onToggleFlag} />

      {/*
        Identity — flexes and truncates; everything qualifying the lead is here.

        **The title is the last thing to lose width, not the first.** The signals
        between it and the note are `shrink-0` (a half-rendered pill or matter
        reference says nothing), and the grey note takes `flex-1` — a zero
        flex-basis, so it grows into the remainder and gives all of it back before
        the title loses a pixel. A weighted `flex-shrink` is not enough here; see
        `LeadSecondaryNote` for why, and for the measurement. The title itself
        needs nothing but the default `shrink` and `min-w-0`: it is the fallback,
        not the priority holder.
      */}
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        <LeadTypeIcon row={lead} />
        <span
          className={cn('text-foreground min-w-0 truncate', rhythm.titleText)}
        >
          {leadRowTitle(lead)}
        </span>
        {!!lead.matterReference && (
          <LeadMatterChip reference={lead.matterReference} />
        )}
        <LeadUnreadReplySignal row={lead} />
        <LeadNewDocumentsSignal row={lead} />
        <LeadOutstandingSignal row={lead} />
        <LeadCostAgreementSignal row={lead} />
        {!!lead.hasRecording && <LeadScribeGlyph />}
        <LeadClosedReason row={lead} />
        <LeadSecondaryNote row={lead} />
      </div>

      <LeadFormCell lead={lead} onSendForm={actions.onSendForm} />
      <LeadMeetingCell
        lead={lead}
        now={now}
        onEditMeeting={actions.onEditMeeting}
      />
      <LeadAgeCell lead={lead} />
      <LeadSolicitorCell lead={lead} />
      <LeadRowActions row={lead} handlers={actions} />
    </div>
  );
}

/**
 * §3.5.10 — the Form column.
 *
 * No form on an open lead is the dashed **`Send`** pill rather than a `—`,
 * because sending one is the next action on most in-intake rows. A closed lead
 * with no form gets the `—`: there is nothing to send.
 *
 * **There is no `Paused` state here.** The spec lists one, but the shipped row
 * DTO carries no chase-pause field — that belongs to the chase engine
 * (LEADS-39 / LF-6006) and has no column to read yet. Rendering it from nothing
 * would be a stub.
 */
function LeadFormCell({
  lead,
  onSendForm
}: {
  lead: Pick<LeadBoardRow, 'formProgress' | 'chaseCount' | 'outcome'>;
  onSendForm?: () => void;
}) {
  const noForm = lead.formProgress == null;

  return (
    <LeadColumnCell className={LEAD_COLUMNS.form} muted={noForm}>
      {noForm ? (
        lead.outcome ? undefined : (
          <LeadDashedPill
            label="Send"
            title="Send the AI intake form to the enquirer"
            icon={<SendIcon className="h-3 w-3" />}
            onClick={onSendForm}
          />
        )
      ) : (
        <span className="inline-flex items-center gap-1.5">
          {lead.chaseCount > 0 && (
            <span
              title={`${lead.chaseCount} automated reminder${lead.chaseCount === 1 ? '' : 's'} sent`}
              className="text-muted-foreground inline-flex shrink-0 items-center gap-1"
            >
              <BellIcon className="h-3 w-3" />
              {lead.chaseCount}
            </span>
          )}
          {/* Green only at 100 — the form is back, which is the fact worth colouring. */}
          <span
            className={
              lead.formProgress === 100 ? 'text-success/80' : undefined
            }
          >
            {lead.formProgress}%
          </span>
        </span>
      )}
    </LeadColumnCell>
  );
}

/**
 * §3.5.11 — the Meeting column, **and the way to change it.**
 *
 * The date/time opens the meeting form directly rather than making you go
 * `⋯` → `Edit meeting details`: a menu is the right home for actions whose
 * object you cannot see, and this cell *is* the object.
 *
 * **The affordance is the drawer's, deliberately reused** — plain text at rest,
 * a blue tint on hover — so "this value is live, click it" means one thing
 * whether you are on the board or in the drawer. It sits at rest rather than
 * looking like a control because the column is mostly *reading*.
 *
 * **A closed lead keeps a plain span.** There is nothing to arrange on a lead
 * that has converted or been lost.
 */
function LeadMeetingCell({
  lead,
  now,
  onEditMeeting
}: {
  lead: Pick<LeadBoardRow, 'nextMeetingAt' | 'nextMeetingIsPast' | 'outcome'>;
  now?: Date;
  onEditMeeting?: () => void;
}) {
  const closed = !!lead.outcome;

  if (!lead.nextMeetingAt) {
    return (
      <LeadColumnCell className={LEAD_COLUMNS.meeting} muted>
        {closed ? undefined : (
          <LeadDashedPill
            label="Meeting"
            title="Set the meeting date and time"
            icon={<PlusIcon className="h-3 w-3" />}
            onClick={onEditMeeting}
          />
        )}
      </LeadColumnCell>
    );
  }

  const label = formatMeetingCell(
    lead.nextMeetingAt,
    lead.nextMeetingIsPast,
    now
  );
  const title = `${lead.nextMeetingIsPast ? 'Met' : 'Meeting'} ${formatSpelledDateTime(lead.nextMeetingAt)}`;
  // Past the meeting the hour answers nothing, so the cell also drops its weight.
  const tone = lead.nextMeetingIsPast
    ? 'text-muted-foreground'
    : 'font-medium text-foreground-secondary';

  return (
    <LeadColumnCell className={LEAD_COLUMNS.meeting}>
      {closed ? (
        <span title={title} className={cn('truncate', tone)}>
          {label}
        </span>
      ) : (
        <button
          type="button"
          title={title}
          aria-label={`Edit meeting details: ${title}`}
          onClick={(e) => {
            // Otherwise the row's own handler opens the drawer behind the
            // dialog, and closing the dialog leaves you somewhere you did not
            // ask for.
            e.stopPropagation();
            onEditMeeting?.();
          }}
          className={cn(
            // `px-1` with no negative margin: the tint keeps a little breathing
            // room, the button's right edge stays flush with the column's, and
            // the 4px inset is invisible against the neighbouring columns.
            'hover:bg-info-subtle hover:text-info-subtle-foreground max-w-full cursor-pointer truncate rounded-md px-1 py-0.5 transition-colors',
            tone
          )}
        >
          {label}
        </button>
      )}
    </LeadColumnCell>
  );
}

/** §3.5.12 — time in the current status. **Bold amber once it is the reason to act.** */
function LeadAgeCell({
  lead
}: {
  lead: Pick<LeadBoardRow, 'daysInStage' | 'isStalled'>;
}) {
  return (
    <LeadColumnCell className={LEAD_COLUMNS.age}>
      <span
        title={`${lead.daysInStage} day${lead.daysInStage === 1 ? '' : 's'} in this status`}
        className={
          lead.isStalled
            ? 'text-warning-subtle-foreground font-bold'
            : undefined
        }
      >
        {formatDays(lead.daysInStage)}
      </span>
    </LeadColumnCell>
  );
}

/**
 * §3.5.13. **The solicitor the enquirer is seeing or is due to see.**
 *
 * **One person, as the All Matters list's initials bubble**, or nothing at all.
 *
 * The column rendered two overlapping `TeamAvatars` — the intake owner beside
 * the booked solicitor — until LF-6054 removed the owner concept: an enquiry has
 * *provenance* (who took the call, now a quiet grey line in the drawer) but
 * nobody accountable for it, so the column had been pairing the one person who
 * matters with one who does not. Losing the pair is what made a single bubble
 * the right shape again, and the one it now uses is the matter list's own rather
 * than a look-alike.
 *
 * **The precedence is the server's.** `row.solicitor` is already resolved to the
 * rendered meeting's booked solicitor, falling back to the lead's preferred one,
 * because that choice depends on which meeting the product renders and the
 * frontend re-derives nothing. See `LeadBoardService.resolveRowSolicitor`.
 *
 * **Nobody chosen renders an empty cell** — see the branch below for why this
 * column departs from the `—` the Form and Meeting columns use. The short of it:
 * that marker is a text idiom, and this column stopped being text.
 */
function LeadSolicitorCell({
  lead
}: {
  lead: Pick<LeadBoardRow, 'solicitor'>;
}) {
  const solicitor = lead.solicitor;

  if (!solicitor) {
    /*
      **Blank, not the cell's `—`.** The dash arrived (LF-6054) as the quieter
      replacement for the sentence `Not decided yet`, and its whole argument was
      about weight *among names*: a phrase at near-name weight competed with the
      names above and below it. This column holds no names any more. Against a
      run of bubbles a dash is a text mark in a graphic column — the one thing on
      that vertical run that is not a circle, so the eye lands on precisely the
      rows with nothing to say. An absent bubble already reads as nobody.

      An empty fragment rather than no children at all: `LeadColumnCell` falls
      back to `—` on nullish children, so this is how "nothing, deliberately" gets
      past a default that exists for the columns that do want it.
    */
    return (
      <LeadColumnCell className={LEAD_COLUMNS.solicitor}>
        <></>
      </LeadColumnCell>
    );
  }

  const name = userDisplayName(solicitor);

  return (
    <LeadColumnCell className={LEAD_COLUMNS.solicitor}>
      {/*
        The All Matters list's own `TeamAvatars` treatment, reused rather than
        re-rolled — a bubble styled to look like it must *be* it
        (`frontend/CLAUDE.md` § "If it looks like a standard, it IS the
        standard"), and initials derived by one helper so two lists cannot
        abbreviate the same person differently.

        `ownerLabel` is not decoration: the component's default tooltip heading is
        `Owner`, which on an enquiry means the *matter* owner and is the exact
        confusion the module bans (`backend/src/leads/CLAUDE.md`).
      */}
      <TeamAvatars assignedSolicitor={name} ownerLabel="Solicitor" />
    </LeadColumnCell>
  );
}

/**
 * The dashed "this is missing, add it" pill, borrowed from the inventory Today
 * view's `+ Add value`: dashed at rest, solid and blue on hover, so an empty
 * slot reads as an invitation rather than as absent data.
 *
 * It renders whether or not a handler is wired, because the pill is **state as
 * well as affordance** — a dashed `Send` says "no form has gone out", which the
 * column's `—` (a closed lead) would otherwise be mistaken for.
 *
 * `Send` takes a paper plane and `+ Meeting` a plus, deliberately: `+` means
 * *add a record that isn't there* and always prefixes a noun, whereas sending a
 * form is an outbound act with a recipient.
 */
function LeadDashedPill({
  label,
  title,
  icon,
  onClick
}: {
  label: string;
  title: string;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className="text-12px border-border-strong text-muted-foreground hover:border-info hover:bg-info-subtle hover:text-info-subtle-foreground inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-dashed px-1.5 py-0.5 font-medium transition-colors hover:border-solid"
    >
      {icon}
      {label}
    </button>
  );
}
